import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import { ApiRequestError, ApiRequestProgress, ApiRequestSucceed } from './saga/api/actions';
import { get, isNull, isPlainObject, isString, merge, reduce, set, unset } from 'lodash';
import { TBaseDataItem, TBaseDataObject, TSiteTemplate } from '../types';
import { LocalStorageLoaded } from './saga/localStorage';
import { templateCollectionMutationSchema } from '../utils/mutationSchema/templateCollectionMutationSchema';
import { IApiStateEntry } from './saga/api/types';

export const reducer = createSlice({
  name: 'store',
  initialState,
  extraReducers(builder) {
    builder.addCase(LocalStorageLoaded, (state, { payload }) => {
      if (payload.jwt) {
        state.access.jwt = payload.jwt;
      }
      if (payload.states && isPlainObject(payload.states)) {
        for (const [key, value] of Object.entries(payload.states)) {
          state.ui.states[key] = value as
            | TBaseDataItem
            | TBaseDataObject
            | TBaseDataItem[]
            | TBaseDataObject[];
        }
      }
    });
    builder.addCase(ApiRequestProgress, (state, { payload }) => {
      state.api[payload.key] = {
        status: 'progress',
        timestamp: Date.now(),
      };
    });
    builder.addCase(ApiRequestError, (state, { payload }) => {
      state.api[payload.key] = {
        status: 'rejected',
        error: payload.error,
        timestamp: Date.now(),
      };
    });
    builder.addCase(ApiRequestSucceed, (state, { payload }) => {
      if (isPlainObject(payload.data)) {
        const collections: Record<string, RegExp[]> = {
          site: [
            /^pages$/,
            /^templates$/,
            /^icons$/,
            /^expressions$/,
            /^locales$/,
            /^settings$/,
            /^regions$/,
          ],
          ui: [/^action$/],
          access: [/^me$/, /^jwt$/, /^roles$/],
          units: [/^[\w\d\-_]+$/],
        };
        const data = payload.data as Record<string, Record<string, TBaseDataObject>>;
        for (const [sectionName, newSection] of Object.entries(data)) {
          const collectionNames = collections[sectionName];
          const newSectionFiltered = reduce(
            newSection,
            (r, v, k) => {
              if (collectionNames.find(item => item.test(k))) return { ...r, [k]: v };
              return { ...r };
            },
            {}
          );
          const oldSection = get(state, sectionName);
          if (sectionName !== 'site') {
            set(state, sectionName, merge(oldSection, newSectionFiltered));
            if ((sectionName === 'access' && isString(newSection.jwt)) || isNull(newSection.jwt)) {
              if (newSection.jwt) {
                localStorage.setItem('jwt', newSection.jwt);
              } else {
                localStorage.removeItem('jwt');
              }
            }
          } else {
            const newTemplates = get(newSectionFiltered, 'templates') as
              | Record<string, TSiteTemplate>
              | undefined;
            if (!newTemplates) {
              set(state, sectionName, merge(oldSection, newSectionFiltered));
            } else {
              const schemedTemplates = templateCollectionMutationSchema(newTemplates);
              set(newSectionFiltered, 'templates', schemedTemplates);
              set(state, sectionName, merge(oldSection, newSectionFiltered));
            }
          }
        }
      }
      state.api[payload.key] = {
        status: 'succeed',
        meta: payload.meta,
        timestamp: Date.now(),
      };
    });
  },
  reducers: {
    setLocale: (state, { payload }: PayloadAction<string>) => {
      if (!payload || typeof payload !== 'string') return;
      state.ui.locale = payload;
    },

    setToken: (state, { payload }: PayloadAction<string | null>) => {
      state.access.jwt = !!payload ? payload : undefined;
    },

    setNavigateTo: (state, { payload }: PayloadAction<string | undefined>) => {
      state.ui.navigateTo = payload;
    },

    setCustomState: (
      state,
      {
        payload,
      }: PayloadAction<{
        name: string;
        value: TBaseDataItem | TBaseDataObject | TBaseDataItem[] | TBaseDataObject[];
      }>
    ) => {
      state.ui.states[payload.name] = payload.value;
    },

    setMe: (state, { payload }: PayloadAction<string | undefined>) => {
      state.access.me = payload;
    },

    setApiState: (state, { payload }: PayloadAction<{ key: string; state?: IApiStateEntry }>) => {
      if (!payload.state) unset(state.api, payload.key);
      set(state.api, payload.key, payload.state);
    },
  },
});

export const {
  setLocale,
  setToken,
  setNavigateTo,
  setCustomState,
  setMe,
  setApiState,
} = reducer.actions;

export default reducer.reducer;
